/**
 * Bestiary of the Lost — entry point.
 *
 * Architecture:
 *   ASCII engine paints the lost painting in a centered ~60vw column.
 *   PreText overlays catalog text in left + right side bands (entries),
 *     or in a single full-width center band (preface + coda).
 *
 * Audio is intentionally omitted from this build (see hermes-skill/
 * bestiary.skill.yaml). The pipeline ships composer + voice polecat
 * source files but the runtime never plays anything; the catalog is
 * presented in silence, the way a museum or a private library would
 * be encountered.
 *
 * The timeline coordinates the ASCII + typography layers.
 */

import { Timeline } from "./timeline.js";
import { initAsciiEngine } from "./ascii/engine.js";
import { initTypographicLayer } from "./pretext-layer.js";
import { runBootSequence } from "./boot-sequence.js";
import { runClosingSequence } from "./closing-sequence.js";

async function loadManifest() {
  const res = await fetch("/manifest/manifest.json");
  if (!res.ok) throw new Error(`Failed to load manifest: ${res.status}`);
  const raw = await res.json();
  // Filter out comment / placeholder scenes
  raw.scenes = (raw.scenes || []).filter((s) => s && s.type);
  return raw;
}

async function bootstrap() {
  const manifest = await loadManifest();

  const ascii = initAsciiEngine(document.getElementById("ascii-mount"));
  const typographic = initTypographicLayer({
    leftBand: document.getElementById("text-band-left"),
    rightBand: document.getElementById("text-band-right"),
    centerBand: document.getElementById("text-band-center"),
  });

  const timeline = new Timeline({ manifest, ascii, typographic });

  // Debug hook — gives the console access to the live engine. Only used
  // for inspecting canvas state and prototyping animation bounds.
  window.__ascii = ascii;
  window.__timeline = timeline;

  const splash = document.getElementById("splash");
  const playBtn = document.getElementById("play-btn");

  // Single entry point — click the button, press Enter, or press Space
  // while the splash is showing. Guarded so it only fires once.
  let started = false;
  async function start() {
    if (started) return;
    started = true;
    splash.classList.add("hidden");
    await runBootSequence();
    await timeline.play();
    await runClosingSequence();
  }

  playBtn.addEventListener("click", start);
  document.addEventListener("keydown", (e) => {
    if (started) return;
    if (e.code === "Enter" || e.code === "Space" || e.code === "NumpadEnter") {
      e.preventDefault();
      start();
    }
  });
}

bootstrap().catch((err) => {
  console.error("Bestiary failed to bootstrap:", err);
  document.body.innerHTML = `<pre style="padding: 2em;">${
    err.stack || err
  }</pre>`;
});
